import { useState } from "react";
import { Menu, MenuItem } from "@mui/material";

import { Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useDialogs } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { AppButton } from "@components/v2/AppButton";
import { DialogType } from "@interfaces/slices";
import { setDialogConfig, setPrintingMode } from "@store/slices/componentsSlice";
import { Notificacion } from "@components/DialogosSegmentos/Notificacion";
import { DescargarReporte } from "@components/DialogosSegmentos/DescargarReporte";
import { usePostExtractoProspectosMutation } from "@services/api/extras";
import { useLazyGetContractDocsZipLinkQuery, usePostDocumentsZipLinkMutation } from "@services/api/documents";

const OPTIONS = [
    { key: "DOCEXP", value: "Expediente" },
    { key: "DOCVAL", value: "Documentos Valor" },
    // { key: "REPCRE", value: "Reporte Crediticio" },
    // { key: "REPPLD", value: "Reporte PLD" },
    { key: "EXTPRO", value: "Prospectos" },
];

/**
 * V2 de la opción "Descargar" en `@components/CustomHeader.tsx` (fuera de
 * `/solicitudes`) — mismas 5 opciones y misma lógica por caso, pero con
 * `AppButton` + `Menu` de MUI en lugar de `BotonMenu` para encajar con el
 * shell v2.
 */
export const DownloadMenuButton = () => {
    const dispatch = useDispatch();
    const dialogs = useDialogs();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [downloadingDocs, setDownloadingDocs] = useState(false);

    const userSession = useSelector((state: RootState) => state.session.user);
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const [triggerPostDocsZipLink] = usePostDocumentsZipLinkMutation();
    const [triggerPostExtractoProspectos] = usePostExtractoProspectosMutation();
    const [triggerGetContractDocsZipLink] = useLazyGetContractDocsZipLinkQuery();

    const handleClose = () => setAnchorEl(null);

    const handleOnOptionSelected = async (option: { key: string; value: string }) => {
        handleClose();

        switch (option.key) {
            case "DOCEXP":
                try {
                    setDownloadingDocs(true);
                    await triggerPostDocsZipLink({
                        applicationId: applicationSelected.id,
                        userId: userSession.id,
                    }).unwrap();

                    await dialogs.open(Notificacion, {
                        type: "INFO",
                        text: "La solicitud de generación del expediente se realizo con éxito, en breve llegará un correo con el link para su descarga.",
                    });
                } catch {
                    await dialogs.open(Notificacion, {
                        type: "WARNING",
                        text: "No se pudo obtener el zip del expediente.",
                    });
                }
                setDownloadingDocs(false);
                break;
            case "DOCVAL":
                try {
                    setDownloadingDocs(true);
                    const link = await triggerGetContractDocsZipLink({
                        applicationId: applicationSelected.id,
                    }).unwrap();

                    if (!link.startsWith("https://")) {
                        throw new Error("URL de descarga inválida");
                    }

                    const linkElement = document.createElement("a");
                    linkElement.href = link;

                    document.body.appendChild(linkElement);

                    linkElement.click();

                    document.body.removeChild(linkElement);
                } catch {
                    await dialogs.open(Notificacion, {
                        type: "WARNING",
                        text: "No se pudo obtener el zip con los documentos.",
                    });
                }
                setDownloadingDocs(false);
                break;
            case "REPCRE":
                dispatch(setPrintingMode(true));
                await dialogs.open(DescargarReporte, {
                    fullScreen: true,
                });
                break;
            case "REPPLD":
                dispatch(setPrintingMode(true));
                dispatch(
                    setDialogConfig({
                        shown: true,
                        fullscreen: true,
                        type: DialogType.DESCARGAR_REPORTE_PLD,
                        props: undefined,
                    }),
                );
                break;
            case "EXTPRO":
                try {
                    const result = await triggerPostExtractoProspectos(applicationSelected.id).unwrap();

                    const rawBase64 = result.data.startsWith("data:")
                        ? result.data.replace(/^data:[^;]+;base64,/, "")
                        : result.data;
                    const link = document.createElement("a");
                    link.href = `data:text/csv;base64,${rawBase64}`;
                    link.download = `prospectos_${applicationSelected.company_source.toLowerCase()}_${applicationSelected.id_application_company}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } catch {
                    await dialogs.open(Notificacion, {
                        type: "WARNING",
                        text: "Ocurrio un error al generar el CSV de prospectos.",
                    });
                }
                break;
        }
    };

    return (
        <>
            <AppButton
                tone="ghost"
                startIcon={<Download size={14} />}
                sx={{ height: 34, fontSize: "0.75rem" }}
                loading={downloadingDocs}
                onClick={(event) => setAnchorEl(event.currentTarget)}
            >
                Descargar
            </AppButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {OPTIONS.map((option) => (
                    <MenuItem key={option.key} onClick={() => handleOnOptionSelected(option)}>
                        {option.value}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
